import { useEffect, useRef, useState, type FormEvent } from "react";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { Button, IconButton, Input, useLayout } from "@agentsaas/ui";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { useThemePref } from "../app/theme";
import { KEY_SEQ, TerminalSession, ctrlChar } from "./session";

function cssVar(name: string) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

function xtermTheme() {
  return {
    background: cssVar("--color-surface") || "#ffffff",
    foreground: cssVar("--color-text") || "#000000",
    cursor: cssVar("--color-accent") || "#3b5bdb",
    selectionBackground: cssVar("--color-accent-soft") || "#e7ecff",
  };
}

/**
 * 终端：xterm.js + fit，输出走 WS 二进制帧。
 * 手机上以只读为主：xterm 不接收键盘输入，底部是一排快捷键（Tab / Ctrl / Esc / 方向键）和一个命令输入框。
 */
export function TerminalView({ aid, onExit }: { aid: string; onExit?(code: number): void }) {
  const { t } = useT();
  const errText = useErrorText();
  const { api, ws } = useServices();
  const mobile = useLayout() === "mobile";
  const [pref] = useThemePref();
  const host = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const sessionRef = useRef<TerminalSession | null>(null);
  const [status, setStatus] = useState<"connecting" | "open" | { exit: number } | { error: string }>("connecting");
  const [ctrl, setCtrl] = useState(false);
  const [line, setLine] = useState("");

  useEffect(() => {
    const term = new Terminal({
      fontFamily: cssVar("--font-mono") || "monospace",
      fontSize: 13,
      cursorBlink: !mobile,
      disableStdin: mobile,
      convertEol: false,
      theme: xtermTheme(),
      scrollback: 5000,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(host.current!);
    termRef.current = term;
    const safeFit = () => {
      try {
        fit.fit();
      } catch {
        /* 容器尚无尺寸 */
      }
    };
    safeFit();
    const session = new TerminalSession(api, ws, aid, {
      write: (s) => term.write(s),
      ready: () => setStatus((s) => (s === "connecting" ? "open" : s)),
      exit: (code) => {
        setStatus({ exit: code });
        onExit?.(code);
      },
    });
    sessionRef.current = session;
    session.open(term.cols, term.rows).catch((e) => setStatus({ error: errText(e) }));
    const d1 = term.onData((d) => session.input(d));
    const d2 = term.onResize(({ cols, rows }) => session.resize(cols, rows));
    const ro = new ResizeObserver(() => safeFit());
    ro.observe(host.current!);
    if (!mobile) term.focus();
    return () => {
      ro.disconnect();
      d1.dispose();
      d2.dispose();
      session.close();
      term.dispose();
      termRef.current = null;
    };
    // 会话只在挂载时建立一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aid]);

  useEffect(() => {
    if (termRef.current) termRef.current.options.theme = xtermTheme();
  }, [pref]);

  const send = (s: string) => sessionRef.current?.input(s);
  const sendKey = (k: keyof typeof KEY_SEQ) => {
    setCtrl(false);
    send(KEY_SEQ[k]);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ctrl && line.length === 1) {
      send(ctrlChar(line));
      setCtrl(false);
    } else send(`${line}\r`);
    setLine("");
  };

  return (
    <div className="term" data-testid="terminal" data-status={typeof status === "string" ? status : "exit" in status ? "exited" : "error"}>
      {status === "connecting" ? <div className="banner small muted">{t("term.connecting")}</div> : null}
      {typeof status === "object" ? (
        <div className={`banner ${"error" in status ? "banner--danger" : "banner--warning"}`} role="status">
          {"error" in status ? status.error : t("term.exited", { code: status.exit })}
        </div>
      ) : null}
      <div className="term__screen" ref={host} />
      {mobile ? (
        <div className="term__mobile">
          <div className="term__keys" role="toolbar" aria-label={t("term.shortcuts")}>
            <Button size="sm" onClick={() => sendKey("tab")}>
              {t("term.key.tab")}
            </Button>
            <Button size="sm" variant={ctrl ? "primary" : "secondary"} aria-pressed={ctrl} onClick={() => setCtrl((v) => !v)} data-testid="key-ctrl">
              {t("term.key.ctrl")}
            </Button>
            <Button size="sm" onClick={() => sendKey("esc")}>
              {t("term.key.esc")}
            </Button>
            <IconButton icon="chevronLeft" label={t("term.key.left")} variant="secondary" onClick={() => sendKey("left")} />
            <IconButton icon="chevronDown" label={t("term.key.down")} variant="secondary" onClick={() => sendKey("down")} />
            <IconButton icon="chevronDown" label={t("term.key.up")} variant="secondary" className="flip-y" onClick={() => sendKey("up")} />
            <IconButton icon="chevronRight" label={t("term.key.right")} variant="secondary" onClick={() => sendKey("right")} />
          </div>
          <form className="term__input" onSubmit={submit}>
            <Input
              value={line}
              onChange={(e) => setLine(e.target.value)}
              aria-label={t("term.input")}
              placeholder={t("term.input")}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="send"
              data-testid="term-input"
            />
            <IconButton type="submit" icon="send" label={t("term.send")} variant="primary" />
          </form>
        </div>
      ) : null}
    </div>
  );
}
